import React, { useState } from "react";
import { Mail, Lock, User, Building2, Phone, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { Logo } from "./Logo";

const GoogleGlyph = (props) => (
  <svg viewBox="0 0 48 48" width="18" height="18" {...props}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.7 34.6 27 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.6 5.6C41.3 36 44 30.6 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

export default function AuthScreen() {
  const { login, register, loginWithGoogle } = useAuth();
  const [screen, setScreen] = useState("choose"); // choose | email
  const [mode, setMode] = useState("login");        // login | register
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", companyName: "", mobile: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    try {
      setBusy(true);
      if (mode === "login") {
        await login(form.email.trim(), form.password);
        toast.success("Welcome back");
      } else {
        await register({ name: form.name.trim(), email: form.email.trim(), password: form.password, companyName: form.companyName.trim(), mobile: form.mobile.trim() });
        toast.success("Workspace created");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#0B0F17] text-slate-100 px-5 relative overflow-hidden" data-testid="auth-screen">
      <div className="absolute -top-32 -left-24 w-[26rem] h-[26rem] rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="absolute -bottom-32 -right-24 w-[24rem] h-[24rem] rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-8">
          <Logo size={56} className="shadow-xl shadow-emerald-500/20 mb-4" />
          <h1 className="font-head font-extrabold text-2xl text-white">Welcome to SSG AI</h1>
          <p className="text-slate-400 text-sm mt-1.5">India-first Universal AI & Data Intelligence</p>
        </div>

        {screen === "choose" && (
          <div className="space-y-3" data-testid="auth-choose">
            <button data-testid="auth-google-button" onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl bg-white text-slate-800 font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg">
              <GoogleGlyph /> Continue with Google
            </button>
            <button data-testid="auth-email-button" onClick={() => { setScreen("email"); setMode("login"); }}
              className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl bg-white/5 border border-white/10 text-slate-100 font-semibold text-sm hover:bg-white/10 transition-colors">
              <Mail size={17} /> Continue with Email
            </button>
            <p className="text-[11px] text-slate-500 text-center pt-3 leading-relaxed">
              By continuing you agree to our <a href="/legal.html" target="_blank" rel="noreferrer" className="text-slate-400 underline">Terms</a> and <a href="/privacy.html" target="_blank" rel="noreferrer" className="text-slate-400 underline">Privacy Policy</a>.
            </p>
          </div>
        )}

        {screen === "email" && (
          <div data-testid="auth-email-panel">
            <button onClick={() => setScreen("choose")} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4" data-testid="auth-back-button">
              <ArrowLeft size={14} /> Back
            </button>
            <div className="flex items-center gap-2 mb-5 p-1 rounded-xl bg-white/5 border border-white/10">
              <button data-testid="auth-tab-login" onClick={() => setMode("login")}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-emerald-500 text-white" : "text-slate-400 hover:text-white"}`}>Sign in</button>
              <button data-testid="auth-tab-register" onClick={() => setMode("register")}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-emerald-500 text-white" : "text-slate-400 hover:text-white"}`}>Create account</button>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
              {mode === "register" && (
                <>
                  <Field icon={User} placeholder="Your name" value={form.name} onChange={set("name")} testid="auth-name" />
                  <Field icon={Building2} placeholder="Company / workspace name" value={form.companyName} onChange={set("companyName")} testid="auth-company" />
                  <Field icon={Phone} type="tel" placeholder="Mobile number" value={form.mobile} onChange={set("mobile")} testid="auth-mobile" />
                </>
              )}
              <Field icon={Mail} type="email" placeholder="Work email" value={form.email} onChange={set("email")} testid="auth-email" required />
              <Field icon={Lock} type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} testid="auth-password" required />
              <button data-testid="auth-submit" type="submit" disabled={busy}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-colors shadow-lg shadow-emerald-500/20 disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={16} /></>}
              </button>
            </form>
            <p className="text-[11px] text-slate-500 mt-4 text-center leading-relaxed">
              {mode === "register" ? "You'll become the admin of a new workspace." : "Your data is isolated to your company workspace."}
            </p>
          </div>
        )}

        <div className="text-center text-[11px] text-slate-600 mt-8">© {new Date().getFullYear()} SSG AI · Bharat Intelligence</div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-3.5 focus-within:border-emerald-500/50 focus-within:ring-2 focus-within:ring-emerald-500/15 transition-all">
      <Icon size={16} className="text-slate-500 shrink-0" />
      <input {...props} data-testid={testid}
        className="flex-1 bg-transparent py-3 text-sm text-slate-100 placeholder-slate-500 focus:outline-none" />
    </div>
  );
}
